import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DamsServices } from '../services/api/dams/DamServices';

interface IDamContextData {
  damId: string | null;
  damName: string | null;
}

// Diferente do AuthContext, aqui NÃO lançamos erro fora do provider: o LayoutBase
// (consumidor deste contexto) é usado também em páginas sem barragem em contexto
// (empresas, usuários, login etc.), então o valor padrão precisa ser "vazio e seguro".
const DamContext = createContext<IDamContextData>({ damId: null, damName: null });

interface IDamProviderProps {
  children: React.ReactNode;
}

// Extrai o id da barragem a partir do path atual, cobrindo tanto '/information_dam/:id/...'
// quanto '/action_plan/:id/...' — funciona independente do nome do parâmetro de rota
// (':id' na maioria das rotas, ':dam' em algumas), já que opera sobre o path bruto.
function extractDamIdFromPathname(pathname: string): string | null {
  const match = pathname.match(/^\/(?:information_dam|action_plan)\/([^/]+)/);
  return match ? match[1] : null;
}

export const DamProvider: React.FC<IDamProviderProps> = ({ children }) => {
  const location = useLocation();
  const [damId, setDamId] = useState<string | null>(null);
  const [damName, setDamName] = useState<string | null>(null);

  const currentDamId = useMemo(
    () => extractDamIdFromPathname(location.pathname),
    [location.pathname],
  );

  useEffect(() => {
    if (!currentDamId) {
      setDamId(null);
      setDamName(null);
      return;
    }

    if (currentDamId === damId) return; // já carregado/carregando para esta barragem

    let cancelled = false;
    setDamId(currentDamId);
    setDamName(null);

    DamsServices.getById(currentDamId).then(response => {
      if (cancelled) return;
      if (!(response instanceof Error)) {
        setDamName(response.nome);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [currentDamId]);

  const value = useMemo(() => ({ damId, damName }), [damId, damName]);

  return (
    <DamContext.Provider value={value}>
      {children}
    </DamContext.Provider>
  );
};

export const useDamContext = () => useContext(DamContext);
